// connectState.js — one tenant-level fact: "has any endpoint ever reported?"
//
// Two consumers share it. The App landing gate opens a fresh tenant's front
// door on the Connect surface instead of the chat hub (whose starter queries
// all return nothing until an agent reports), and the Connect surface itself
// flips the fact live as its check-in poll sees the first endpoint arrive.
//
// Cache shape: the reactive `useSyncExternalStore` mode (signalCatalog.js),
// NOT the fire-once userInfo/permissions mode — the value legitimately
// changes after mount (empty → connected while the Connect surface polls)
// and the landing gate must follow without a reload.
//
// Failure posture: the gate FAILS OPEN. A 403 (role can't read endpoint
// metadata), a network error, or a pre-RBAC tenant all resolve to "unknown"
// and the landing renders exactly as it does today. Only a confirmed zero
// engages the Connect front door.
import { useEffect, useSyncExternalStore } from "react";
import { restGet } from "./restClient.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

function emptyState(status = "idle") {
    return { status, connected: null, total: null };
}

let _state = emptyState();
const _listeners = new Set();
let _inflight = null;

function _snapshot() { return _state; }

function _emit() {
    for (const l of _listeners) l(_state);
}

// Fetch the endpoint count once. Idempotent: concurrent callers share the
// in-flight promise. A confirmed CONNECTED result is terminal — a tenant
// never un-enrolls its whole fleet mid-session, and re-gating a connected
// tenant is the one failure mode this module must never have. A confirmed
// zero and an "unknown" both stay non-terminal so a later hydrate (remount,
// retry) can observe the fleet coming online.
export function hydrateConnectState() {
    if (_inflight) return _inflight;
    if (_state.status === "ready" && _state.connected) return Promise.resolve();
    if (_state.status === "idle" || _state.status === "unknown") {
        _state = { ..._state, status: "loading" };
        _emit();
    }
    const epoch = getAuthIdentityEpoch();
    const request = restGet("/v1/endpoints/metadata", { page: 0, page_size: 1 })
        .then((resp) => {
            if (getAuthIdentityEpoch() !== epoch) return;
            const total = Number(resp?.total ?? 0);
            _state = { status: "ready", connected: total > 0, total };
            _emit();
        })
        .catch(() => {
            if (getAuthIdentityEpoch() !== epoch) return;
            // 403 / network / auth-race — all fail open, none engage the gate.
            _state = emptyState("unknown");
            _emit();
        })
        .finally(() => { if (_inflight === request) _inflight = null; });
    _inflight = request;
    return request;
}

// The Connect surface's check-in poll calls this the moment it sees the
// first endpoint, so the App gate (a different component tree) learns the
// tenant is connected without its own refetch.
export function markTenantConnected(total = 1) {
    if (_state.status === "ready" && _state.connected) return;
    _state = { status: "ready", connected: true, total: Number(total) || 1 };
    _emit();
}

export function subscribeConnectState(cb) {
    _listeners.add(cb);
    return () => _listeners.delete(cb);
}

// The gate predicate, pure so the truth table is unit-testable: only a
// CONFIRMED zero engages. idle/loading/unknown/connected all leave the
// landing untouched.
export function shouldEngageConnectGate(state) {
    return state?.status === "ready" && state?.connected === false;
}

// ── The front-door pin ───────────────────────────────────────────
//
// The gate must survive a mid-session page reload (Vite's dep-optimizer
// reload in dev, a user refresh in prod) and a one-off fetch failure.
// Deriving it from the in-memory latch alone can't: a reload restarts
// from idle, and a single errored fetch fails open — ejecting a user
// mid-mission onto the chat hub. So the moment the front door engages,
// pin it in sessionStorage. A pinned tab holds the Connect surface on
// the param-less landing until the tenant is CONFIRMED connected (the
// unpin effect in App.jsx); unknown/loading/error all keep the mission
// on screen, where the surface's own polling self-heals. Per-tab by
// design: a fresh tab re-derives from the fetch as before.
const FRONT_DOOR_PIN_KEY = "dashboard:connect-front-door";

export function pinConnectFrontDoor() {
    try {
        sessionStorage.setItem(FRONT_DOOR_PIN_KEY, "1");
    } catch {}
}

export function unpinConnectFrontDoor() {
    try {
        sessionStorage.removeItem(FRONT_DOOR_PIN_KEY);
    } catch {}
}

export function isConnectFrontDoorPinned() {
    try {
        return sessionStorage.getItem(FRONT_DOOR_PIN_KEY) === "1";
    } catch {
        return false;
    }
}

onAuthIdentityChange(() => {
    _state = emptyState();
    _inflight = null;
    unpinConnectFrontDoor();
    _emit();
});

export function _resetConnectStateForTests() {
    _state = emptyState();
    _inflight = null;
    _listeners.clear();
    unpinConnectFrontDoor();
}

export function _seedConnectStateForTests(next) {
    _state = { ...emptyState("ready"), ...next };
    _emit();
}

// Subscribe a component to the fact and (when `enabled`) ensure it's
// hydrated. `enabled` exists so App only spends the fetch when the caller
// is actually landing on the dashboard front door.
export function useConnectState({ enabled = true } = {}) {
    const state = useSyncExternalStore(subscribeConnectState, _snapshot, _snapshot);
    useEffect(() => {
        if (enabled) hydrateConnectState();
    }, [enabled]);
    return state;
}
